import React, { useLayoutEffect } from 'react';
import { nanoid } from 'nanoid';
import { Link } from 'gatsby';

import { Navigation } from '../../interfaces';
import { LinkLogo } from './LinkLogo';
import { URLEnums } from '../../enum/URLEnum';

interface Props {
  menu: Map<Navigation, Navigation[]>;
}

export const DeviceMenu = ({ menu }: Props) => {
  const [menuIsOpen, setMenuIsOpen] = React.useState(false);
  const hamburgerClass = menuIsOpen ? 'hamburger-container close' : 'hamburger-container';

  function toggleMenu() {
    setMenuIsOpen(!menuIsOpen);
  }

  if (typeof window !== 'undefined') {
    window.onresize = () => {
      if (window.innerWidth > 1023 && menuIsOpen) {
        toggleMenu();
      }
    };
  }

  useLayoutEffect(() => {
    if (!menuIsOpen) {
      document.body.classList.remove('no-scroll');
    } else {
      document.body.classList.add('no-scroll');
    }
  }, [menuIsOpen]);

  const scrollToElement = (hash: string, e: { preventDefault: () => void }) => {
    e.preventDefault();

    setMenuIsOpen(false);

    const topOffset = () => {
      switch (hash) {
        case '#challenges':
          return 85;
        case '#overview':
          return 85;
        default:
          return 0;
      }
    };

    const scrollTarget = document.querySelector(hash);

    const elementPosition = scrollTarget!.getBoundingClientRect().top;

    const offsetPosition = elementPosition! + topOffset();

    window.scrollBy({
      top: offsetPosition,
      behavior: 'smooth',
    });
  };

  const isHomePage = () => {
    const url = typeof window !== 'undefined' ? window.location.href : '';

    return url.split('/').at(-1) === '' || url.split('/').at(-1)?.includes('#');
  };

  const isInternalAnchorLink = (url: string | undefined) => {
    return url?.substring(0, 1) === '#';
  };

  return (
    <div className="main-navigation-device-view">
      <div className={hamburgerClass} onClick={() => toggleMenu()}>
        <div className="hamburger">
          <span className="hamburger-stripe" />
        </div>
      </div>
      <div className={menuIsOpen ? 'main-navigation-devices devices-opened' : 'main-navigation-devices'}>
        <LinkLogo linkClass="link-logo" />
        <div className="main-navigation-devices-inner">
          <div className="main-navigation-devices-menu">
            {Array.from(menu.entries()).map(([key, values]) => {
              return (
                <div className="main-navigation-item opened" key={nanoid()}>
                  {values.length ? (
                    <>
                      <div className="main-navigation-item-head">
                        <div className="title">{key.title}</div>
                      </div>
                      <div className="links-container">
                        {values.map((link) => {
                          return link.link?.uri.startsWith('/') ? (
                            <Link
                              key={link.id}
                              to={link.link?.uri || ''}
                              className="sub-link"
                              rel="noreferrer"
                              aria-label={`Go to section ${link.title}`}
                              onClick={() => toggleMenu()}
                            >
                              {link.title}
                            </Link>
                          ) : (
                            <a
                              href={link.link?.uri}
                              key={link.id}
                              className="sub-link"
                              target="_blank"
                              rel="noreferrer"
                              onClick={() => toggleMenu()}
                              aria-label={`Go to ${link.title}`}
                              key={link.id}
                            >
                              {link.title}
                            </a>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <Link
                      to={key.link?.uri || ''}
                      target="_self"
                      className="link"
                      rel="noreferrer"
                      onClick={(e) => {
                        if (isHomePage() && isInternalAnchorLink(key.link?.uri)) {
                          scrollToElement(key.link?.anchor || '#flow', e);
                        }

                        toggleMenu();
                      }}
                    >
                      {key.title}
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div className="main-navigation-devices-footer">
          <a className="main-navigation-link link-button" href={URLEnums.ContactUs} aria-label="Contact us">
            Contact us
          </a>
        </div>
      </div>
    </div>
  );
};
